import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { formatDateTime } from 'utils/helper';

const PRINT_INFORMATION_COLUMN_COUNT = 3;
const PRINT_INFORMATION_MAX_COLUMNS = Array.from(
  { length: PRINT_INFORMATION_COLUMN_COUNT },
  (_, index) => index
);

const getShootingLocations = selectedBudget =>
  selectedBudget?.shooting_locations?.filter(location => !!location) || [];

const getShootingLocationLabel = shootingLocations =>
  shootingLocations?.length > 1 ? 'Shooting Locations' : 'Shooting Location';

const getFringeLabel = fringe =>
  fringe?.display_name || fringe?.label || fringe?.name || '';

const fringeShape = PropTypes.shape({
  display_name: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
});

const printInformationSectionShape = PropTypes.shape({
  columns: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
});

const compactOptionalRows = rows =>
  rows.filter(row => row !== null && row !== undefined);

const cloneColumns = columns =>
  PRINT_INFORMATION_MAX_COLUMNS.map(index =>
    Array.isArray(columns?.[index])
      ? columns[index].map(item => String(item ?? ''))
      : []
  );

export const getDefaultJobInformation = selectedBudget => {
  const budgetDate = formatDateTime(
    selectedBudget?.updated_on || selectedBudget?.created_on,
    'MM/DD/YYYY'
  );

  return {
    columns: [
      compactOptionalRows([
        budgetDate ? `Date ${budgetDate}` : 'Date',
        'Job Name',
        'Advertiser',
        'Advertiser Address',
      ]),
      ['Advertiser Job Id', 'Agency Job Id', 'Production Job Ids:'],
      ['Brand', 'Product', 'Traffic Information:'],
    ],
  };
};

export const getDefaultProductionInformation = selectedBudget => {
  const shootingLocations = getShootingLocations(selectedBudget);
  const shootingLocationLabel = getShootingLocationLabel(shootingLocations);
  const director = selectedBudget?.director
    ? `Director(s) ${selectedBudget.director}`
    : 'Director(s)';
  const shootingLocation = shootingLocations?.length
    ? `${shootingLocationLabel}: ${shootingLocations.join(', ')}`
    : null;
  const budgetVersion = selectedBudget?.budget_version
    ? `Budget Version: ${selectedBudget.budget_version}`
    : '';

  return {
    columns: [
      compactOptionalRows([
        'Production Company',
        'Editorial Co',
        'VFX Co',
        'Music Co',
        '',
        'Production Contact',
        'Email',
        'Editorial Producer',
        'Email',
        'VFX Producer',
        'Email',
        'Music Producer',
        'Email',
        director,
        shootingLocation,
        '',
        'editor',
        "Add'l Post Creative(s)",
      ]),
      [
        'Agency',
        'Agency Address',
        '',
        'Agency Producer',
        'Phone',
        'Email',
        'Agency Address',
        '',
        'Agency Creative Team',
        '',
        'Agency Business Mgr',
        'Phone',
        'Email',
      ],
      [
        'Pre-Production Days',
        'Build & Strike Days',
        'Pre Light:',
        'Stage days:',
        'Location Days:',
        budgetVersion,
        '',
        'Spot Title & Lengths:',
      ],
    ],
  };
};

export const normalizePrintInformationSection = (
  section,
  fallbackSection = { columns: [[], [], []] }
) => {
  if (!section || !Array.isArray(section?.columns)) {
    return { columns: cloneColumns(fallbackSection?.columns) };
  }

  const fallbackColumns = cloneColumns(fallbackSection?.columns);
  return {
    columns: PRINT_INFORMATION_MAX_COLUMNS.map(index => {
      const column = section.columns[index];
      if (!Array.isArray(column)) {
        return fallbackColumns[index] || [];
      }

      return column.map(item => String(item ?? ''));
    }),
  };
};

export const updatePrintInformationCell = (
  section,
  columnIndex,
  itemIndex,
  value
) => {
  const columns = cloneColumns(section?.columns);
  if (!columns[columnIndex]) {
    columns[columnIndex] = [];
  }
  columns[columnIndex][itemIndex] = value;
  return { columns };
};

const ProductionInfoHeaders = ({
  selectedBudget,
  includeFringeDetails,
  fringes,
}) => {
  const shootingLocations = getShootingLocations(selectedBudget);
  const shootingLocationLabel = getShootingLocationLabel(shootingLocations);

  return (
    <div className="print-layout__settings">
      <div className="left-block">
        <div className="left-block__item">
          Budget Dated:{' '}
          {formatDateTime(
            selectedBudget?.updated_on || selectedBudget?.created_on,
            'MM/DD/YYYY'
          )}
        </div>
        {selectedBudget?.producers?.map((producer, index) => (
          <div className="left-block__item" key={`${producer}-${index}`}>
            Producer: {producer || ''}
          </div>
        ))}

        {!!selectedBudget?.director && (
          <div className="left-block__item">
            Director: {selectedBudget?.director || ''}
          </div>
        )}

        {!!shootingLocations?.length && (
          <div className="left-block__item">
            {shootingLocationLabel}: {shootingLocations.join(', ')}
          </div>
        )}

        {includeFringeDetails && (
          <div
            style={{
              display: 'inline-block',
              maxWidth: '60%',
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            <strong style={{ marginRight: '5px' }}>Fringes:</strong>
            {fringes.length ? (
              fringes.map((fringe, index) => (
                <span
                  key={`${getFringeLabel(fringe)}-${index}`}
                  style={{ marginRight: '5px' }}
                >
                  {getFringeLabel(fringe).replace(/\r?\n/g, ' ')}
                  {index < fringes.length - 1 && ','}
                </span>
              ))
            ) : (
              <span
                style={{
                  color: 'gray',
                  fontStyle: 'italic',
                  marginLeft: '5px',
                }}
              >
                No fringes available
              </span>
            )}
          </div>
        )}
      </div>
      <div className="right-block">
        {!!selectedBudget?.shooting_days && (
          <div className="right-block__item">
            Shooting Days: {selectedBudget?.shooting_days || ''}
          </div>
        )}
        {!!selectedBudget?.script_pages && (
          <div className="right-block__item">
            Script Pages: {selectedBudget?.script_pages || ''}
          </div>
        )}
        {!!selectedBudget?.principal_photography && (
          <div className="right-block__item">
            Principal Photography: {selectedBudget?.principal_photography || ''}
          </div>
        )}
        {!!selectedBudget?.wrap_principal && (
          <div className="right-block__item">
            Wrap Principal: {selectedBudget?.wrap_principal || ''}
          </div>
        )}
        {!!selectedBudget?.script_version && (
          <div className="right-block__item">
            Script Version: {selectedBudget?.script_version || ''}
          </div>
        )}
        {!!selectedBudget?.budget_version && (
          <div className="right-block__item">
            Budget Version: {selectedBudget?.budget_version || ''}
          </div>
        )}
      </div>
    </div>
  );
};

const PrintInformationLine = ({ value, onChange, canEdit, ariaLabel }) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = 'auto';
    textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
  }, [value]);

  if (!canEdit) {
    return <span className="print-info-text">{value}</span>;
  }

  return (
    <textarea
      ref={textareaRef}
      className="print-info-editable"
      value={value}
      onChange={event => onChange?.(event.target.value)}
      aria-label={ariaLabel}
      maxLength={500}
      rows={1}
    />
  );
};

const PrintInformationColumns = ({
  section,
  onChange,
  canEdit,
  contentClassName,
  columnFooters,
}) => {
  const normalizedSection = normalizePrintInformationSection(section);
  const columnClassNames = ['left-block', 'center-block', 'right-block'];

  return (
    <div className={contentClassName}>
      {normalizedSection.columns.map((column, columnIndex) => (
        <div
          className={columnClassNames[columnIndex]}
          key={`print-info-column-${columnIndex}`}
        >
          {column.map((value, itemIndex) => (
            <div
              className="sub-block__item print-info-row"
              key={`print-info-cell-${columnIndex}-${itemIndex}`}
            >
              <PrintInformationLine
                value={value}
                canEdit={canEdit}
                ariaLabel={`Print information column ${columnIndex + 1} row ${
                  itemIndex + 1
                }`}
                onChange={nextValue =>
                  onChange?.(
                    updatePrintInformationCell(
                      normalizedSection,
                      columnIndex,
                      itemIndex,
                      nextValue
                    )
                  )
                }
              />
            </div>
          ))}
          {columnFooters?.[columnIndex]}
        </div>
      ))}
    </div>
  );
};

const FringeDetails = ({ fringes }) => (
  <div className="sub-block__item print-info-row print-info-fringes">
    <span>Fringes:</span>
    {fringes.length > 0 ? (
      <span className="print-info-fringes__items">
        {fringes.map((fringe, index) => (
          <span key={`fringe-${index}`}>{getFringeLabel(fringe)}</span>
        ))}
      </span>
    ) : (
      <span className="print-info-fringes__empty">No fringes available</span>
    )}
  </div>
);

PrintInformationLine.defaultProps = {
  onChange: null,
};

PrintInformationLine.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  canEdit: PropTypes.bool.isRequired,
  ariaLabel: PropTypes.string.isRequired,
};

PrintInformationColumns.defaultProps = {
  onChange: null,
  columnFooters: null,
};

PrintInformationColumns.propTypes = {
  section: PropTypes.shape({
    columns: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
  }).isRequired,
  onChange: PropTypes.func,
  canEdit: PropTypes.bool.isRequired,
  contentClassName: PropTypes.string.isRequired,
  columnFooters: PropTypes.objectOf(PropTypes.node),
};

FringeDetails.propTypes = {
  fringes: PropTypes.arrayOf(fringeShape).isRequired,
};

const AicpJobInformation = ({
  section,
  onChange,
  canEdit,
  includeFringeDetails,
  fringes,
}) => (
  <div className="job-info">
    <div className="job-info-header">Job Information</div>
    <PrintInformationColumns
      section={section}
      onChange={onChange}
      canEdit={canEdit}
      contentClassName="job-info-content"
      columnFooters={{
        1: includeFringeDetails ? <FringeDetails fringes={fringes} /> : null,
      }}
    />
  </div>
);

const AicpProductionInformation = ({ section, onChange, canEdit }) => (
  <div className="production-info">
    <div className="production-info-header">Production Information</div>
    <PrintInformationColumns
      section={section}
      onChange={onChange}
      canEdit={canEdit}
      contentClassName="production-info-content"
    />
  </div>
);

const ProductionInfoBlocks = ({
  selectedBudget,
  showProductionInfoHeaders,
  showJobInformationSection,
  showProductionInformationSection,
  jobInformationSection,
  productionInformationSection,
  onJobInformationChange,
  onProductionInformationChange,
  canEditProductionInfoSections,
  includeFringeDetails,
  fringes,
}) => {
  if (
    !showProductionInfoHeaders &&
    !showJobInformationSection &&
    !showProductionInformationSection
  ) {
    return null;
  }

  return (
    <>
      {showProductionInfoHeaders && (
        <ProductionInfoHeaders
          selectedBudget={selectedBudget}
          includeFringeDetails={includeFringeDetails}
          fringes={fringes}
        />
      )}
      {(showJobInformationSection || showProductionInformationSection) && (
        <div className="aicp-print-layout__settings">
          {showJobInformationSection && (
            <AicpJobInformation
              section={
                jobInformationSection ||
                getDefaultJobInformation(selectedBudget)
              }
              onChange={onJobInformationChange}
              canEdit={canEditProductionInfoSections}
              includeFringeDetails={includeFringeDetails}
              fringes={fringes}
            />
          )}
          {showProductionInformationSection && (
            <AicpProductionInformation
              section={
                productionInformationSection ||
                getDefaultProductionInformation(selectedBudget)
              }
              onChange={onProductionInformationChange}
              canEdit={canEditProductionInfoSections}
            />
          )}
        </div>
      )}
    </>
  );
};

const budgetShape = PropTypes.shape({
  budget_version: PropTypes.string,
  created_on: PropTypes.string,
  director: PropTypes.string,
  principal_photography: PropTypes.string,
  producers: PropTypes.arrayOf(PropTypes.string),
  script_pages: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  script_version: PropTypes.string,
  shooting_days: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  shooting_locations: PropTypes.arrayOf(PropTypes.string),
  updated_on: PropTypes.string,
  wrap_principal: PropTypes.string,
});

ProductionInfoHeaders.defaultProps = {
  selectedBudget: null,
};

ProductionInfoHeaders.propTypes = {
  selectedBudget: budgetShape,
  includeFringeDetails: PropTypes.bool.isRequired,
  fringes: PropTypes.arrayOf(fringeShape).isRequired,
};

AicpJobInformation.defaultProps = {
  onChange: null,
};

AicpJobInformation.propTypes = {
  section: printInformationSectionShape.isRequired,
  onChange: PropTypes.func,
  canEdit: PropTypes.bool.isRequired,
  includeFringeDetails: PropTypes.bool.isRequired,
  fringes: PropTypes.arrayOf(fringeShape).isRequired,
};

AicpProductionInformation.defaultProps = {
  onChange: null,
};

AicpProductionInformation.propTypes = {
  section: printInformationSectionShape.isRequired,
  onChange: PropTypes.func,
  canEdit: PropTypes.bool.isRequired,
};

ProductionInfoBlocks.defaultProps = {
  selectedBudget: null,
  jobInformationSection: null,
  productionInformationSection: null,
  onJobInformationChange: null,
  onProductionInformationChange: null,
  canEditProductionInfoSections: false,
};

ProductionInfoBlocks.propTypes = {
  selectedBudget: budgetShape,
  showProductionInfoHeaders: PropTypes.bool.isRequired,
  showJobInformationSection: PropTypes.bool.isRequired,
  showProductionInformationSection: PropTypes.bool.isRequired,
  jobInformationSection: printInformationSectionShape,
  productionInformationSection: printInformationSectionShape,
  onJobInformationChange: PropTypes.func,
  onProductionInformationChange: PropTypes.func,
  canEditProductionInfoSections: PropTypes.bool,
  includeFringeDetails: PropTypes.bool.isRequired,
  fringes: PropTypes.arrayOf(fringeShape).isRequired,
};

export default ProductionInfoBlocks;
